import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {Box, Label, RelativeTime, Text} from '@primer/react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import type {IssueBodyHeader$key} from './__generated__/IssueBodyHeader.graphql'
import {IssueBodyHeaderActions, type IssueBodyHeaderActionsProps} from './IssueBodyHeaderActions'
import {IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

export type IssueBodyHeaderProps = {
  additionalActions?: React.ReactNode
  comment: IssueBodyHeader$key
  isPullRequest?: boolean
  actionProps?: IssueBodyHeaderActionsProps
}
export function IssueBodyHeader({additionalActions, comment, isPullRequest, actionProps}: IssueBodyHeaderProps) {
  const data = useFragment(
    graphql`
      fragment IssueBodyHeader on Comment {
        ...MarkdownEditHistoryViewer_comment
        createdAt
        viewerDidAuthor
        author {
          ...IssueBodyHeaderAuthor
        }
      }
    `,
    comment,
  )
  return (
    <Box
      sx={{
        backgroundColor: data.viewerDidAuthor ? 'accent.subtle' : 'canvas.subtle',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottom: '1px solid',
        borderBottomColor: data.viewerDidAuthor ? 'accent.muted' : 'border.muted',
        color: 'fg.muted',
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        fontSize: 1,
        gap: 0,
        py: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          minHeight: 'var(--control-small-size, 28px)',
          display: 'flex',
          justifyContent: 'space-between',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            px: 3,
            flexGrow: 1,
            flexShrink: 1,
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {data.author && <IssueBodyHeaderAuthor author={data.author} />}
          <Box
            as="h3"
            sx={{
              display: 'flex',
              flexDirection: ['column', 'column', 'row', 'row'],
              flexWrap: 'wrap',
              columnGap: 1,
              overflow: 'hidden',
              fontSize: 'unset',
              fontWeight: 'unset',
              margin: 'unset',
            }}
          >
            {data.author && <IssueBodyHeaderAuthor showLogin author={data.author} />}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                fontSize: [0, 1, 1, 1],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Text sx={{fontSize: 'inherit', whiteSpace: 'nowrap', display: ['none', 'inline', 'inline', 'inline']}}>
                {isPullRequest ? 'created' : 'opened this issue'}
              </Text>
              <RelativeTime sx={{fontSize: 'inherit'}} date={new Date(data.createdAt)} />
              {/** As Pull Request Issue Body Descriptions show Author label to the right of created at date, we need to render this additional label */}
              {isPullRequest && <Label sx={{ml: 2}}>Author</Label>}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            gap: 1,
            pl: [0, 3, 3],
            pr: 2,
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <MarkdownEditHistoryViewer editHistory={data} />
          {additionalActions}
          {actionProps && <IssueBodyHeaderActions {...actionProps} />}
        </Box>
      </Box>
    </Box>
  )
}

try{ IssueBodyHeader.displayName ||= 'IssueBodyHeader' } catch {}